import React, { useContext } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../../lib/queryClient';
import { AuthProvider, useAuth } from '../../contexts/AuthContext';
import { DarkModeProvider } from '../../contexts/DarkModeContext';
import { UXPreferencesProvider } from '../../contexts/UXPreferencesContext';
import { NotificationProvider } from '../../contexts/NotificationContext';
import { NoteModeProvider } from '../../contexts/NoteModeContext';
import { ConversationProvider } from '../../contexts/ConversationContext';
import { ToastSettingsProvider } from '../../contexts/ToastSettingsContext';
import ToastSettingsContext from '../../contexts/ToastSettingsContext';

import { PatientProvider } from '../../contexts/PatientContext';
import { TabProvider } from '../../contexts/TabContext';
import { PermissionProvider } from '../../contexts/PermissionContext';
import { DialogProvider } from '../../contexts/DialogContext';
import { AIContextProvider } from '../../contexts/AIContext';
import { CalendarOptionsProvider } from '../../contexts/CalendarOptionsContext';
import { ActiveChartProvider } from '../../contexts/ActiveChartContext';
import { BriefingProvider } from '../../contexts/BriefingContext';
import { getSessionUuid } from '../../utils/identity';
import { useSessionExpiryWarning } from '../../hooks/useSessionExpiryWarning';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { ToastContainer } from "react-toastify";
import { logger } from "../../utils/logger";

/**
 * AppProviders - Wraps the app with all context providers
 * 
 * CRITICAL: Provider order must be preserved exactly as shown below.
 * Changing the order can break the application.
 * 
 * Provider Hierarchy:
 * 1. AuthProvider - Must be first (other providers depend on auth state)
 * 2. DarkModeProvider - Theme provider
 * 3. UXPreferencesProvider - Text size, density, and layout preferences
 * 4. DialogProvider - Dialog management
 * 5. NoteModeProvider - Note writing mode state
 * 6. ToastSettingsProvider - Toast notification settings
 * 7. AppContentWithNotifications - Nested providers that depend on auth
 *    - NotificationProvider
 *    - PermissionProvider
 *    - ConversationProvider (requires userId from auth)
 *    - AIContextProvider (requires user from auth)
 *    - BriefingProvider (shared briefing data for Dashboard + AI Assistant)
 *    - PatientProvider
 *    - TabProvider
 */
export function AppProviders({ children }) {
  return (
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <DarkModeProvider>
        <UXPreferencesProvider>
          <DialogProvider>
            <NoteModeProvider>
              <ToastSettingsProvider>
                <AppContentWithNotifications>
                  {children}
                </AppContentWithNotifications>
              </ToastSettingsProvider>
            </NoteModeProvider>
          </DialogProvider>
        </UXPreferencesProvider>
      </DarkModeProvider>
    </AuthProvider>
    </QueryClientProvider>
  );
}

/**
 * AppContentWithNotifications - Nested providers that depend on auth state
 * This component waits for auth to load before rendering dependent providers
 */
function AppContentWithNotifications({ children }) {
  return (
    <AuthenticatedAppGate>
      <NotificationProvider>
        <AuthenticatedAppContent>{children}</AuthenticatedAppContent>
      </NotificationProvider>
    </AuthenticatedAppGate>
  );
}

export function AuthenticatedAppGate({ children }) {
  const { loading } = useAuth();

  if (loading) {
    return <div className="p-4">Checking login status...</div>;
  }

  return children;
}

function AuthenticatedAppContent({ children }) {
  const { user } = useAuth();
  useSessionExpiryWarning();
  const idleTimeoutDialog = useIdleTimeout();

  return (
    <PermissionProvider>
      <ConversationProvider userId={getSessionUuid(user)}>
        <AIContextProvider user={user || null}>
          <BriefingProvider>
          {/* ActiveChartProvider is the single source of truth for the
              active chart; PatientProvider delegates selection to it, so
              it must wrap PatientProvider. */}
          <ActiveChartProvider user={user}>
            <PatientProvider>
              <TabProvider>
                <CalendarOptionsProvider>
                  {children}
                  <ToastNotifications />
                  {idleTimeoutDialog}
                </CalendarOptionsProvider>
              </TabProvider>
            </PatientProvider>
          </ActiveChartProvider>
          </BriefingProvider>
        </AIContextProvider>
      </ConversationProvider>
    </PermissionProvider>
  );
}

/**
 * ToastNotifications - Toast container with settings from context
 */
function ToastNotifications() {
  const toastContext = useContext(ToastSettingsContext);
  
  if (!toastContext) {
    logger.warn('ToastNotifications: ToastSettingsContext not available, using default settings');
    return (
      <ToastContainer 
        position="bottom-right" 
        autoClose={3000} 
      />
    );
  }
  
  const { persistToasts } = toastContext;
  
  return (
    <ToastContainer 
      position="bottom-right" 
      autoClose={persistToasts ? false : 3000} 
    />
  );
}
